import type { AbsoluteWorkspacePath } from "@codesandbox/pitcher-client";
import { FileMarkdownPreview } from "components/Devtools/FileDevtool/FileMarkdownPreview";
import { createEditorInputFromTab } from "components/Layout/Panels/VSCode/editorSync";
import { getPanelContent, getTabName } from "components/Layout/utils";
import type { RegisterEditorParams } from "environment-interface/monacoEditor/browser/services/editor/vscode/tools/custom-editor";
import type { Tab } from "features/types/tabs";
import { usePitcher } from "features/utils/usePitcher";
import React, { useCallback, useMemo } from "react";
import * as vscode from "vscode";

import type { ComponentProps } from "./CustomEditor";
import { CustomEditor } from "./CustomEditor";

export const MARKDOWN_EDITOR_ID = "codesandbox.editors.markdown";

export const CustomEditors: React.FC = () => {
  return (
    <>
      <CodeSandboxDevtools />
      <CustomEditor
        Component={InteractiveMarkdownWrapper}
        getName={(relativePath) =>
          relativePath ? `${relativePath.path.split("/").pop()}` : "Markdown"
        }
        globPattern="*.md"
        id={MARKDOWN_EDITOR_ID}
        name="Interactive Markdown"
        paneLabel="CodeSandbox Interactive Markdown"
        serializer={{
          serialize(editor): string | undefined {
            const resource = editor.resource;
            return resource?.toString();
          },

          deserialize(
            _instantiationService,
            serializedEditor: string,
          ): vscode.Uri | undefined {
            const uri = vscode.Uri.parse(serializedEditor);
            return uri;
          },
        }}
      />
    </>
  );
};

const CodeSandboxDevtools: React.FC = () => {
  const getName = useCallback((resource?: vscode.Uri | undefined) => {
    if (!resource) {
      return "Loading...";
    }

    const query = new URLSearchParams(resource.query);
    const rawTab = query.get("tab");

    if (!rawTab) {
      return "Cannot find tab";
    }
    const tab: Tab = JSON.parse(rawTab);

    if (!tab) {
      return "Cannot find tab";
    }

    if (tab.type === "TASK_PORT") {
      return `Preview (${tab.port})`;
    }

    return getTabName(tab);
  }, []);

  const getIcon = useCallback((resource?: vscode.Uri | undefined) => {
    if (!resource) {
      return undefined;
    }

    const query = new URLSearchParams(resource.query);
    const rawTab = query.get("tab");

    if (!rawTab) {
      return "Cannot find tab";
    }
    const tab: Tab = JSON.parse(rawTab);

    if (tab.type === "TASK_PORT" || tab.type === "UNASSIGNED_PORT") {
      return `browser`;
    }

    return undefined;
  }, []);

  const serializer = useMemo<RegisterEditorParams["serializer"]>(
    () => ({
      serialize(editor) {
        const resource = editor.resource;
        if (!resource) {
          return undefined;
        }

        const query = new URLSearchParams(resource.query);
        const rawTab = query.get("tab");

        return rawTab || undefined;
      },

      deserialize(_instantiationService, serializedEditor) {
        const tab = JSON.parse(serializedEditor);

        const input = createEditorInputFromTab(tab);
        if (!input) {
          return undefined;
        }

        if (!("resource" in input)) {
          return undefined;
        }

        return input.resource;
      },
    }),
    [],
  );

  return (
    <CustomEditor
      Component={InnerCodeSandboxDevtool}
      getIcon={getIcon}
      getName={getName}
      globPattern="csb-tab:/**"
      id="codesandbox.editors.codesandbox-tab"
      name="CodeSandbox Tab"
      paneLabel="CodeSandbox Devtool"
      serializer={serializer}
      disablePadding
    />
  );
};

const InnerCodeSandboxDevtool: React.FC<ComponentProps> = React.memo(
  ({ resource }) => {
    const topLeftPanel = { id: "top-left-panel", tabs: [] };

    if (!resource) {
      return <div>Loading...</div>;
    }

    const query = new URLSearchParams(resource.query);
    const rawTab = query.get("tab");

    if (!rawTab) {
      return <div>Cannot find tab</div>;
    }
    const tab: Tab = JSON.parse(rawTab);
    if (!tab) {
      return <div>Cannot find tab</div>;
    }

    return (
      <div style={{ width: "100%", height: "100%" }}>
        {getPanelContent(
          { ...tab, id: "INNER_CODESANDBOX_DEVTOOL" },
          topLeftPanel,
        )}
      </div>
    );
  },
);

const InteractiveMarkdownWrapper: React.FC<ComponentProps> = React.memo(
  ({ resource }) => {
    const pitcher = usePitcher();
    if (!resource) {
      return <div>Loading...</div>;
    }

    const relativePath = pitcher.clients.fs.absoluteToRelativeWorkspacePath(
      resource.path as AbsoluteWorkspacePath,
    );

    return (
      <div style={{ width: "100%", height: "100%" }}>
        <FileMarkdownPreview filepath={relativePath} />
      </div>
    );
  },
);
